/** @jsx jsx */
import React, { useState, useEffect, useRef } from "react";
import { Box, Button, ContainerProps, jsx } from "theme-ui";
import {
  useConfigure,
  useInfiniteHits,
  useInstantSearch,
  useCurrentRefinements,
  useClearRefinements,
  useSearchBox,
} from "react-instantsearch-hooks-web";
import { FilterView } from "@shared-components/Algolia/FilterView";
import { Autocomplete } from "@shared-components/Algolia/Autocomplete";
import {
  AlgoliaWrapper,
  searchClient,
} from "@shared-components/Algolia/AlgoliaWrapper";
import { Sorting } from "@shared-components/Algolia/Sorting";
import { PresetFilters } from "@shared-components/Algolia/PresetFilters";
import { Hits } from "@shared-components/Algolia/Hits";
import type { Hit } from "react-instantsearch-core";
import { Container } from "@shared-components/Container/Container";
import { Grid } from "@shared-components/Grid/Grid";
import { mainContainerStyles } from "./styles";
import { BottleDetails } from "@modules/BottleDetails/BottleDetails";
import { Portal } from "@shared-components/Portal/Portal";
import { Button as CustomButton } from "@shared-components/Button/Button";
import { Icon } from "@shared-components/Icon/Icon";
import SearchIcon from "@svg-icons/search-icon.component.svg";
import FilterIcon from "@svg-icons/filter-icon.component.svg";
import { Text } from "@shared-components/Text/Text";
import "@styles/AlgoliaStyles.css";
import { useWindowSize } from "../../shared/hooks/useWindowSize/useWindowSize";
import { split } from "lodash";
import CloseIcon from "@svg-icons/close-icon.component.svg";
import { AutocompleteApi, getAlgoliaResults } from "@algolia/autocomplete-js";
import { motion } from "framer-motion";
import { BaseItem } from "@algolia/autocomplete-core";
import type { CurrentRefinementsConnectorParamsRefinement } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import {
  KioskProvider,
  useKioskContext,
} from "@shared-components/Algolia/KioskContext";
import { DICTIONARY_MAP } from "../..//carlsberg-headless/dictionary-map";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { AlgoliaBottle } from "src/carlsberg-headless/types/modules/digital-memories";

const hitsPerPage = 30;

const CustomKiosk = () => {
  useConfigure({
    hitsPerPage,
  });

  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();

  const [show, setShow] = useState(false);
  const autocompleteRef = useRef<AutocompleteApi<BaseItem> | null>(null);
  const [currentBottleIndex, setCurrentBottleIndex] = useState<number>(0);
  const windowSize = useWindowSize();
  const isMobile = windowSize.width && windowSize.width < 768;
  const { results, status } = useInstantSearch();
  const { showMore, isLastPage, hits } = useInfiniteHits<AlgoliaBottle>();
  const [showFilter, setShowFilter] = useState(false);
  const [animateListOut, setAnimateListOut] = useState(false);
  const [newBottle, setNewBottle] = useState<AlgoliaBottle | null>(null);
  const [activeLetter, setActiveLetter] = useState<string | undefined>(
    undefined
  );
  const { refine: setQuery } = useSearchBox();
  const currentRefinements = useCurrentRefinements();
  const { refine: clearAll } = useClearRefinements();
  const hitsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLDivElement>(null);

  const showMoreClick = (e: any) => {
    showMore();
    e.blur();
  };

  const getAutocompleteSources = ({ query }: { query: string }) => [
    {
      sourceId: "cb-autocomplete",
      getItems() {
        const filters = currentRefinements.items.map((refinement) => {
          return refinement.refinements.map(
            (refine) => `${refinement.attribute}:${refine.value}`
          );
        });
        return getAlgoliaResults({
          searchClient,
          queries: [
            {
              indexName: "bottle_collection",
              query,
              params: {
                filters: filters.join(" AND "),
                distinct: 1,
                hitsPerPage: 8,
              },
            },
          ],
        });
      },
      onSelect(params: any) {
        params.setActiveItemId(params.item.objectID as number);
        setQuery(params.item.name as string);
        hitsRef?.current?.scrollIntoView({
          behavior: "smooth",
        });
      },
      templates: {
        item({ item, components }: { item: any; components: any }) {
          return (
            <div className="aa-ItemContent text-left">
              <div className="aa-ItemTitle">
                <components.Highlight hit={item} attribute="name" />
              </div>
            </div>
          );
        },
      },
    },
  ];

  useEffect(() => {
    if (isMobile) {
      setShowFilter(false);
    }
  }, [isMobile, windowSize]);

  const handleActiveLetter = (letter?: string) => {
    if (activeLetter === letter) {
      setActiveLetter(undefined);
    } else {
      setActiveLetter(letter);
    }
  };

  const openDetail = (index: number, id: string) => {
    history.pushState(null, "", `#${id}`);
    setShow(true);
    setCurrentBottleIndex(index);
    document.body.style.overflow = "hidden";
  };

  const toggleDetail = () => {
    document.body.style.overflow = !show ? "hidden" : "auto";
    if (show) {
      setAnimateListOut(false);
    }
    setShow(!show);
  };

  const animateCardsOut = () => {
    setAnimateListOut(true);

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true);
      }, 500);
    });
  };

  useEffect(() => {
    if (window.location.hash) {
      if (hits && hits.length !== 0) {
        const urlId = split(window.location.hash, "#")[1];
        if (urlId === "undefined") return;
        const bottleIndex = hits.findIndex(
          (bottle) => bottle.id.toString() === urlId
        );

        if (bottleIndex >= 0) {
          setCurrentBottleIndex(bottleIndex);
          setShow(true);
          document.body.style.overflow = "hidden";
        } else {
          fetch(
            `https://europe-west3-cb-bottle-collection.cloudfunctions.net/bottles/api/${urlId}`
          )
            .then((response) => response.text())
            .then((data) => {
              if (data) {
                setNewBottle(JSON.parse(data));
              }
            });
        }
      }
    }
  }, [hits, window.location.hash]);

  useEffect(() => {
    if (newBottle) {
      setShow(true);
      document.body.style.overflow = "hidden";
    }
  }, [newBottle]);

  const handleBottleIndex = (direction: "next" | "prev" | undefined) => {
    let newId;
    if (direction === "next") {
      if (currentBottleIndex + 1 !== hits.length) {
        setCurrentBottleIndex((prevIndex) => prevIndex + 1);
        newId = hits[currentBottleIndex + 1] && hits[currentBottleIndex + 1].id;
      } else if (!isLastPage) {
        showMore();
      }
    } else if (direction === "prev" && currentBottleIndex - 1 !== -1) {
      setCurrentBottleIndex((prevIndex) => prevIndex - 1);
      newId = hits[currentBottleIndex - 1].id;
    }
    history.replaceState(null, "", `${location.href.split("#")[0]}#${newId}`);
  };

  const handleFilterClick = () => {
    animateCardsOut().then(() => {
      if (showFilter) {
        setActiveLetter(undefined);
      }

      setShowFilter((prev) => !prev);

      setAnimateListOut(false);
    });
  };

  const handleInputEvents = (
    inputEvent: "focus" | "blur",
    _autocompleteInstance?: AutocompleteApi<BaseItem>
  ) => {
    if (inputEvent === "blur") {
      autocompleteRef.current?.setIsOpen(false);
    }
  };

  const { abvRangeElement, sizeRangeElement, yearRangeElement } =
    useKioskContext();

  const clearRangeRefinementRange = (
    refinements: CurrentRefinementsConnectorParamsRefinement[],
    refine: (refinement: CurrentRefinementsConnectorParamsRefinement) => void,
    attribute: "year" | "abv" | "sizegroup"
  ) => {
    const resetRangeInput = {
      year: () => yearRangeElement.current.reset(),
      abv: () => abvRangeElement.current.reset(),
      sizegroup: () => sizeRangeElement.current.reset(),
    };

    resetRangeInput[attribute]();
    refinements.forEach((refinement) => refine(refinement));
  };

  const narrowAnimation = {
    hidden: { opacity: 0, y: 50, ease: "cubic-bezier(0.35, 1, 0.45, 1)" },
    show: {
      opacity: 1,
      y: 0,
      ease: "cubic-bezier(0.35, 1, 0.45, 1)",
    },
  };
  const filterAnimation = {
    hidden: { opacity: 0, ease: "cubic-bezier(0.35, 1, 0.45, 1)" },
    show: {
      opacity: 1,
      ease: "cubic-bezier(0.35, 1, 0.45, 1)",
    },
  };

  const transition = {
    duration: 0.5,
  };

  const totalHits =
    results.nbSortedHits === 1000 ? results.nbHits : results.nbSortedHits;

  return (
    <React.Fragment>
      <Box id={"Kiosk-module"} sx={{ backgroundColor: "lightGray" }}>
        <Container sx={{ backgroundColor: "lightGray" }}>
          <Box>
            <Box
              className="search"
              sx={{
                mt: [2, 5],
                mb: [0, 5],
                display: "flex",
                justifyContent: "space-between",
                flexWrap: ["wrap", "wrap", "nowrap"],
              }}
            >
              <Box
                ref={searchInputRef}
                sx={{
                  position: "relative",
                  display: "flex",
                  background: "white",
                  border: "4px solid",
                  borderRadius: 0,
                  mb: [1, 1, 0],
                  borderColor: "darkGreen",
                  width: ["100%", "100%", "33%"],
                }}
              >
                <Icon
                  svg={SearchIcon}
                  sx={{
                    zIndex: 10,
                    px: ["0.5rem", "1rem"],
                    py: "1rem",
                    height: "25px",
                    width: "auto",
                  }}
                />
                <Autocomplete
                  openOnFocus={true}
                  onBlur={(autocompleteInstance) => {
                    handleInputEvents("blur", autocompleteInstance);
                  }}
                  onFocus={(autocompleteInstance) => {
                    handleInputEvents("focus", autocompleteInstance);
                    if (isMobile) {
                      searchInputRef?.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                    }
                  }}
                  onReset={() => {
                    setQuery("");
                  }}
                  onSubmit={({ state }) => {
                    setQuery(state.query);
                  }}
                  onStateChange={(state) => {
                    if (state.prevState.query !== state.state.query) {
                      state.refresh();
                    }
                  }}
                  detachedMediaQuery={"none"}
                  onInit={(autocompleteInstance) => {
                    autocompleteRef.current = autocompleteInstance;
                  }}
                  getSources={getAutocompleteSources}
                />
              </Box>
              {!isMobile && (
                <CustomButton
                  variant={showFilter ? "greenSelected" : "green"}
                  outline
                  sx={{
                    mr: 1,
                    ml: 1,
                    display: ["none", "none", "flex"],
                    borderRadius: 0,
                    span: {
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      ml: 1,
                    },
                  }}
                  icon={<Icon svg={FilterIcon} />}
                  onClick={handleFilterClick}
                >
                  {get(
                    DICTIONARY_MAP.kiosk.kiosk_filters_filters,
                    currentCulture
                  )}
                </CustomButton>
              )}
              <PresetFilters />
            </Box>
          </Box>
          <Grid
            sx={{
              position: "relative",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 0,
            }}
          >
            <Box
              sx={{
                position: "relative",
                pr: 4,
              }}
            >
              {!isMobile && (
                <motion.div
                  variants={filterAnimation}
                  initial={"hidden"}
                  animate={showFilter ? "show" : "hidden"}
                  transition={transition}
                >
                  <Box
                    sx={{
                      display: showFilter ? "block" : "none",
                      pointerEvents: showFilter ? "auto" : "none",
                      bottom: 0,
                      zIndex: 10,
                      width: "100%",
                      gridColumn: "1",
                    }}
                  >
                    <FilterView
                      activeLetter={activeLetter}
                      handleActiveLetter={handleActiveLetter}
                    />

                    <Box
                      sx={{
                        mt: 4,
                        left: "auto",
                        right: "auto",
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      <CustomButton
                        variant="green"
                        outline
                        onClick={handleFilterClick}
                      >
                        {get(
                          DICTIONARY_MAP.kiosk.kiosk_filters_close,
                          currentCulture
                        )}
                      </CustomButton>
                    </Box>
                  </Box>
                </motion.div>
              )}
            </Box>
            <Box sx={{ gridColumn: showFilter ? "2" : "-1/1" }}>
              {currentRefinements.items &&
                currentRefinements.items.length !== 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      mb: 5,
                      gap: "10px",
                    }}
                  >
                    {currentRefinements.items.map(
                      ({ refinements, refine, attribute }, key) => {
                        if (
                          attribute === "year" ||
                          attribute === "sizegroup" ||
                          attribute === "abv"
                        ) {
                          let label = "";
                          if (attribute === "abv") {
                            const values = abvRangeElement.current.get();
                            label = `${values[0]} - ${values[1]} %`;
                          }
                          if (attribute === "sizegroup") {
                            const values = sizeRangeElement.current.get();
                            label = `${values[0]} - ${values[1]} cl`;
                          }
                          if (attribute === "year") {
                            const values = yearRangeElement.current.get();
                            label = `${values[0]} - ${values[1]}`;
                          }
                          return (
                            <Button
                              key={key}
                              onClick={() =>
                                clearRangeRefinementRange(
                                  refinements,
                                  refine,
                                  attribute
                                )
                              }
                              sx={{
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 4,
                                backgroundColor: "mediumGray",
                                mr: 1,
                                fontFamily: "NyCarlsbergDisplay-Bold",
                                fontSize: "smText",
                                cursor: "pointer",
                                letterSpacing: "3px",
                                textTransform: "uppercase",
                                gap: "10px",
                              }}
                            >
                              {label}
                              <CloseIcon />
                            </Button>
                          );
                        }

                        return refinements.map((refinementProps, key) => {
                          let label = refinementProps.label as any;
                          switch (refinementProps.label) {
                            case "Most Popular":
                              label = get(
                                DICTIONARY_MAP.kiosk.kiosk_filters_favourites,
                                currentCulture
                              );
                              break;
                            case "Oldest Bottles":
                              label = get(
                                DICTIONARY_MAP.kiosk.kiosk_filters_oldest,
                                currentCulture
                              );
                              break;
                            case "Unique Shapes":
                              label = get(
                                DICTIONARY_MAP.kiosk.kiosk_filters_unique,
                                currentCulture
                              );
                              break;
                          }
                          return (
                            <Button
                              key={key}
                              onClick={() => refine(refinementProps)}
                              sx={{
                                mb: [null, 1],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 4,
                                backgroundColor: "mediumGray",
                                fontFamily: "NyCarlsbergDisplay-Bold",
                                fontSize: "smText",
                                cursor: "pointer",
                                letterSpacing: "3px",
                                textTransform: "uppercase",
                                gap: "10px",
                                transition: "opacity 150ms",
                                "&:hover, &:focus": {
                                  opacity: "0.9",
                                },
                                ":focus": {
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {label}
                              <CloseIcon className="ml-5 w-3.5 stroke-white" />
                            </Button>
                          );
                        });
                      }
                    )}
                    <Button
                      sx={{
                        p: 0,
                        color: "mediumGray",
                        background: "transparent",
                        textDecoration: "underline",
                        letterSpacing: "2px",
                        fontFamily: "NyCarlsbergDisplay-Bold",
                        pl: 2,
                        mb: [null, 1],
                        cursor: "pointer",
                        fontSize: "smText",
                        textTransform: "uppercase",
                        ":focus": {
                          boxShadow: "none",
                        },
                      }}
                      onClick={() => {
                        clearAll();
                        setQuery("");
                        abvRangeElement.current?.reset();
                        sizeRangeElement.current?.reset();
                        yearRangeElement.current?.reset();
                        setActiveLetter(undefined);
                      }}
                    >
                      {get(
                        DICTIONARY_MAP.kiosk.kiosk_filters_remove_filters,
                        currentCulture
                      )}
                    </Button>
                  </Box>
                )}
              <Box
                ref={hitsRef}
                sx={{
                  display: "flex",
                  flexDirection: ["column", "row"],
                  flexWrap: ["wrap", "nowrap"],
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Text
                  sx={{
                    fontFamily: "NyCarlsbergDisplay-Bold",
                    fontSize: ["smText", "paragraphMedium"],
                    m: 0,
                    py: 2,
                    mt: [2, 0],
                    lineHeight: 1.15,
                    order: [4, 0],
                    color: "darkGreen",
                  }}
                >
                  {totalHits}{" "}
                  {totalHits === 1
                    ? get(
                        DICTIONARY_MAP.kiosk.kiosk_bottle_found,
                        currentCulture
                      )
                    : get(
                        DICTIONARY_MAP.kiosk.kiosk_bottles_found,
                        currentCulture
                      )}
                </Text>
                <Sorting />
              </Box>
              <motion.div
                variants={narrowAnimation}
                initial={"show"}
                animate={!animateListOut ? "show" : "hidden"}
                transition={transition}
              >
                <Grid
                  columns={[2, "repeat(auto-fill, minmax(200px, 1fr))"]}
                  gap={[2, 2, 4]}
                >
                  <Hits
                    hits={hits as Hit<AlgoliaBottle>[]}
                    openDetail={openDetail}
                    narrowList={showFilter}
                  />
                </Grid>
              </motion.div>
              {!isLastPage && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    my: 9,
                    mt: 14,
                  }}
                  className="mt-16 flex justify-center"
                >
                  <CustomButton
                    variant="green"
                    outline
                    onClick={(e) => showMoreClick(e.target)}
                  >
                    {get(
                      DICTIONARY_MAP.kiosk.kiosk_filters_loadmore,
                      currentCulture
                    )}
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Container>
      </Box>
      {show && (
        <Portal domElementId={"beerDetail"}>
          <Box sx={mainContainerStyles}>
            <BottleDetails
              totalHits={newBottle ? 1 : totalHits}
              hits={newBottle ? [newBottle] : hits}
              currentBottleIndex={newBottle ? 0 : currentBottleIndex}
              handleBottleIndex={handleBottleIndex}
              toggleDetail={toggleDetail}
              isLastPage={newBottle ? true : isLastPage}
              loading={status === "loading" || status === "stalled"}
              show={show}
            />
          </Box>
        </Portal>
      )}
    </React.Fragment>
  );
};

const Kiosk = (): ContainerProps => {
  return (
    <KioskProvider>
      <AlgoliaWrapper>
        <CustomKiosk />
      </AlgoliaWrapper>
    </KioskProvider>
  );
};

export { Kiosk };
